import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from "src/app/store/notifications/notifications.actions";
import * as HomeActions from 'src/app/store/home/home.actions';
import * as CalendarActions from 'src/app/store/calendar/calendar.actions';
import { Observable, Subject, Subscription, combineLatest, distinctUntilChanged } from 'rxjs';
import { selectScenariosTabIndex } from 'src/app/store/home/home.selector';
import { CalendarOptions, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import allLocales from '@fullcalendar/core/locales-all';
import { TranslateService } from '@ngx-translate/core';
import { CALENDAR_HEADER_TOOLBAR, MAX_SCHEDULE_ON_CALENDAR_PAGE, SCHEDULE_EXCEPTION_COLOR } from 'src/app/conf/schedule';
import { FullCalendarComponent } from '@fullcalendar/angular/full-calendar.component';
import { Schedule } from 'src/app/model/schedule';
import { CalendarException } from 'src/app/model/calendar-exception';
import { EScheduleRepeat } from 'src/app/model/schedule-repeat';
import * as moment from 'moment';
import { selectSchedules } from 'src/app/store/schedule/schedule.selector';
import { selectExceptions } from 'src/app/store/calendar/calendar.selector';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG } from 'src/app/conf/languages';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ScheduleDialogComponent } from '../schedule-dialog/schedule.dialog.component';

@Component({
	selector: 'app-calendar',
	templateUrl: './calendar.component.html',
	providers: [DialogService],
})

export class CalendarComponent implements OnInit, OnDestroy {
	@ViewChild('calendar') calendarComponent: FullCalendarComponent;

	// Observables and subscriptions
	sceneryTabIndex$: Observable<number | undefined>;
	sceneryTabIndexSub: Subscription | undefined;

	evntSub: Subscription | undefined;

	schedules$: Observable<Schedule[] | undefined>;
	schedulesSub: Subscription | undefined;
	schedules: Schedule[];

	exceptions$: Observable<CalendarException[]>;
	exceptionsSub: Subscription | undefined;
	exceptions: CalendarException[];

	exceptionsAsString: string[];

	dateRangeSwitch$ = new Subject<DatesSetArg>();
	dateInfo: DatesSetArg;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	langSub: Subscription;
	calendarLang = 'en-EN';

	supportTabIndex: number;

	// tabIndex$: Observable<number>;

	ref: DynamicDialogRef | undefined;

	calendarOptions: CalendarOptions = {
		locales: allLocales,
		locale: this.calendarLang,
		firstDay: 0,
		contentHeight: 600,
		// height: '100%',
		initialView: 'dayGridMonth',
		headerToolbar: CALENDAR_HEADER_TOOLBAR,
		eventTimeFormat: { hour: '2-digit', minute: '2-digit', meridiem: true },
		plugins: [dayGridPlugin, timeGridPlugin],
		datesSet: (dateInfo: DatesSetArg) => {
			this.dateInfo = dateInfo;
			this.dateRangeSwitch$.next(dateInfo);
		},
		eventClick: (event: EventClickArg) => {
			this.openSchedule(event);
		}
	};

	// Design
	screenWidth: number;
	MIN_WIDTH_MX7 = 1700;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.store.dispatch(NotificationsActions.setTopbarTitleId({
			stringId: 'SCENARIOS'
		}));
		this.subscribeAll();
	}

	openSchedule(info: EventClickArg): void {
		if (this.schedules !== undefined && this.schedules.length > 0) {
			const schedule = this.schedules.find(s => s.Id === info.event.extendedProps['scheduleId']);
			if (schedule) {
				this.ref = this.dialogService.open(ScheduleDialogComponent, {
					header: this.translate.instant('EDIT_SCHEDULE'),
					width: '80vw',
					breakpoints: { '1600px': '98vw' },
					// resizable: true,
					contentStyle: { overflow: 'auto' },
					data: {
						schedule: schedule,
					},
				});
			}
		}
	}

	ngOnDestroy(): void {
		this.sceneryTabIndexSub?.unsubscribe();
		this.exceptionsSub?.unsubscribe();
	}

	initSelectors(): void {
		// Store selectors
		this.schedules$ = this.store.select(selectSchedules);
		this.exceptions$ = this.store.select(selectExceptions);
		this.sceneryTabIndex$ = this.store.select(selectScenariosTabIndex);
	}

	subscribeAll(): void {
		this.sceneryTabIndexSub = this.sceneryTabIndex$?.subscribe(
			async (supportTabIndex: number) => {
				this.supportTabIndex = supportTabIndex;
				if (supportTabIndex === 0) {
					this.dateRangeSwitch$.next(this.dateInfo);
					if (this.calendarComponent !== undefined) {
						setTimeout(() => {
							this.calendarComponent.getApi().updateSize();
						},
							5);
					}
				}
			}
		);

		this.currentLang$ = this.store.select(selectCurrentLang);
		this.langSub = this.currentLang$.subscribe(lang => {
			this.currentLang = lang;
			this.calendarLang = lang.code.slice(0, 2);
			this.dateRangeSwitch$.next(this.dateInfo);
		});

		this.evntSub = combineLatest([
			this.schedules$,
			this.exceptions$,
			this.sceneryTabIndex$,
			this.dateRangeSwitch$,
		]).pipe(
			distinctUntilChanged(),
		).subscribe(([schedules, exceptions, tabIndex, dateRange]) => {
			if (tabIndex !== 0) {
				return;
			}
			if (schedules.length > MAX_SCHEDULE_ON_CALENDAR_PAGE) {
				this.store.dispatch(CalendarActions.calendarBigWarning({
					message: this.translate.instant('CALENDAR_BIG_WARNING', { n: MAX_SCHEDULE_ON_CALENDAR_PAGE })
				}));
			}
			this.schedules = schedules.slice(0, MAX_SCHEDULE_ON_CALENDAR_PAGE);
			this.exceptions = exceptions;
			this.rangeSchedules(dateRange);
		});
	}

	buildEvents(schedule: Schedule, dateRange: DatesSetArg): void {
		const calendarApi = this.calendarComponent.getApi();
		const excDates = this.getExclusionDates(schedule.CalendarId);
		if (schedule.EveryKey) {
			const scheduleDates = this.getEventDates(schedule, dateRange);
			const es = this.getEventsFromSchedule(schedule, scheduleDates, excDates);
			calendarApi?.addEventSource(es);
		} else {
			const scheduleStartAsDate = new Date(schedule.Start);
			const excludedDate = this.dateInExceptions(scheduleStartAsDate, excDates);
			const event: EventInput = {
				title: schedule.Name,
				start: new Date(scheduleStartAsDate.setHours(schedule.Hours, schedule.Minutes)),
				end: new Date(scheduleStartAsDate.setHours(schedule.Hours, schedule.Minutes, 30)),
				color: excludedDate ? SCHEDULE_EXCEPTION_COLOR : schedule.Color,
				classNames: excludedDate ? ['excluded-event'] : [],
				extendedProps: {
					scheduleId: schedule.Id,
				}
			};
			calendarApi?.addEvent(event, true);
		}
	}

	getEventsFromSchedule(schedule: Schedule, scheduleDates: Date[], excDates: string[]): EventInput[] {
		const events: EventInput[] = [];
		for (const scheduleDate of scheduleDates) {
			const excluded = this.dateInExceptions(scheduleDate, excDates);
			const event: EventInput = {
				title: schedule.Name,
				start: new Date(new Date(scheduleDate).setHours(schedule.Hours, schedule.Minutes, 0)),
				end: new Date(new Date(scheduleDate).setHours(schedule.Hours, schedule.Minutes, 30)),
				color: excluded ? SCHEDULE_EXCEPTION_COLOR : schedule.Color,
				classNames: excluded ? ['excluded-event'] : [],
				extendedProps: {
					scheduleId: schedule.Id,
				}
			};
			events.push(event);
		}
		return events;
	}

	getExclusionDates(calendarId: number): string[] {
		const exceptions = this.getCaledarExceptions(this.exceptions, calendarId);
		if (exceptions.length > 0) {
			return exceptions.map(e => e.Date.slice(0, 10));
		}
		return [];
	}

	getCaledarExceptions(exceptions: CalendarException[], calendarId?: number): CalendarException[] {
		let exc = [];
		if (calendarId) {
			exc = exceptions.filter(e => e.CalendarId === calendarId);
		}
		return exc;
	}

	getEventDates(schedule: Schedule, dateRange: DatesSetArg): Date[] {
		const dates: Date[] = [];
		switch (schedule.EveryKey) {
			case EScheduleRepeat.Days:
				return this.daysRepetition(schedule, dateRange);
			case EScheduleRepeat.Weeks:
				return this.weeksRepetition(schedule, dateRange);
			case EScheduleRepeat.Months:
				return this.monthsRepetition(schedule, dateRange);
		}
		return dates;
	}

	// TO FIX: ticket 1024
	daysRepetition(schedule: Schedule, dateRange: DatesSetArg): Date[] {
		const dates = [];
		const date = moment(schedule.Start);
		while (
			this.dateBeforeRangeStart(date, moment(dateRange.start))
		) {
			date.add(schedule.EveryValue, 'days');
		}
		while (
			this.dateBeforeRangeEnd(date, moment(dateRange.end)) &&
			this.dateBeforeScheduleEnd(date, schedule.End)
		) {
			if (this.dateAfterScheduleStart(date, moment(schedule.Start))) {
				dates.push(date.toDate());
			}
			date.add(schedule.EveryValue, 'days');
		}
		return dates;
	}

	weeksRepetition(schedule: Schedule, dateRange: DatesSetArg): Date[] {
		const dates = [];
		const days = schedule.WeekDays.split(',');
		const weekDate = moment(dateRange.start);
		while (
			this.dateBeforeRangeEnd(weekDate, moment(dateRange.end)) &&
			this.dateBeforeScheduleEnd(weekDate, schedule.End)
		) {
			for (const day of days) {
				const date = weekDate.weekday(+day);
				if (
					this.dateAfterScheduleStart(date, moment(schedule.Start)) &&
					this.dateBeforeScheduleEnd(date, schedule.End)
				) {
					dates.push(date.toDate());
				}
			}
			weekDate.add(schedule.EveryValue, 'weeks');
		}
		return dates;
	}

	// TO FIX: December 2024 (1st day as first cell in visualization) -> ticket 1024
	monthsRepetition(schedule: Schedule, dateRange: DatesSetArg): Date[] {
		const dates = [];
		const monthDate = moment(dateRange.start); // October 2024: Sun Sep 29 2024
		const lastDayOfMonth = monthDate.clone().endOf('month');

		// condizione while funziona (controlla che le date siano dentro alla visualizzazione mensile del calendar)
		while (this.dateBeforeRangeEnd(monthDate, moment(dateRange.end))) {
			const scheduleMonthDay = schedule.MonthDay;
			const date = lastDayOfMonth.add(scheduleMonthDay, 'days'); // October 2024: Mon Sep 30 2024
			if (
				this.dateAfterRangeStart(date, moment(dateRange.start)) &&
				this.dateBeforeRangeEnd(date, moment(dateRange.end)) &&
				this.dateAfterScheduleStart(date, moment(schedule.Start)) &&
				this.dateBeforeScheduleEnd(date, schedule.End)
			) {
				dates.push(date.toDate());
			}
			monthDate.add(schedule.EveryValue, 'months');
		}
		return dates;
	}

	dateInExceptions(date: Date, excDates: string[]): boolean {
		const formattedDate = this.formatDate(date);
		return excDates.includes(formattedDate);
	}

	dateAfterRangeStart(date: moment.Moment, rangeStart: moment.Moment): boolean {
		return date.isSameOrAfter(rangeStart);
	}

	dateBeforeRangeEnd(date: moment.Moment, rangeEnd: moment.Moment): boolean {
		return date.isSameOrBefore(rangeEnd);
	}

	dateBeforeRangeStart(date: moment.Moment, rangeStart: moment.Moment): boolean {
		return date.isBefore(rangeStart);
	}

	extractDate(dateString: string): string {
		const words = dateString.split(' ');
		const extractedDate = words.slice(0, 4).join(' ');
		return extractedDate;
	}

	dateAfterScheduleStart(date: moment.Moment, scheduleStart: moment.Moment): boolean {
		let datestr: string
		datestr = date["_d"].toString();
		datestr = this.extractDate(datestr);

		let scheduleStartstr: string
		scheduleStartstr = scheduleStart["_d"].toString();
		scheduleStartstr = this.extractDate(scheduleStartstr);

		return date.isSameOrAfter(scheduleStart) || datestr === scheduleStartstr;
	}

	dateBeforeScheduleEnd(date: moment.Moment, scheduleEnd?: string): boolean {
		if (!scheduleEnd) {
			return true;
		}
		const se = moment(scheduleEnd);
		return !se || date.isSameOrBefore(se);
	}

	dateRangeOutOfScope(schedule: Schedule, dateRange: DatesSetArg): boolean {
		return (schedule.End && new Date(schedule.End) < dateRange.start) || new Date(schedule.Start) > dateRange.end;
	}

	rangeSchedules(dateRange): void {
		const calendarApi = this.calendarComponent.getApi();
		calendarApi?.setOption('locale', this.calendarLang);
		calendarApi?.removeAllEvents();
		for (const schedule of this.schedules) {
			if (this.dateRangeOutOfScope(schedule, dateRange)) {
				continue;
			}
			this.buildEvents(schedule, dateRange);
		}
	}

	formatDate(d: Date): string {
		let month = '' + (d.getMonth() + 1);
		let day = '' + d.getDate();
		const year = d.getFullYear();

		if (month.length < 2) {
			month = '0' + month;
		}
		if (day.length < 2) {
			day = '0' + day;
		}
		return [year, month, day].join('-');
	}

	layoutMarginResponsive(): string {
		return (this.screenWidth >= this.MIN_WIDTH_MX7) ? "mx-7" : "mx-2";
	}

	changeTab(e) {
		this.store.dispatch(HomeActions.selectScenariosTab({ index: e.index }));
	}
}
